/* eslint-disable react-hooks/set-state-in-effect */
/**
 * PricingTiersGrid Component
 *
 * Classic 4-tier pricing grid with interactive calculators.
 * - Free: Limited credits (credit-based)
 * - Starter: Bilder/Monat → Credits → Preis (credit-based)
 * - Pro: Produkte + Channels (dimension-based) + PIM/DAM
 * - Enterprise: Produkte + Channels (dimension-based) + PIM/DAM + Custom Features
 *
 * WCAG 2.1 Level AA + BFSG compliant
 */

import { useState, useEffect } from 'react';
import { Check, Zap, Rocket, Building2, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/v2/ui/Card';
import { Button } from '@/components/v2/ui/Button';
import { Badge } from '@/components/v2/ui/Badge';
import { Label } from '@/components/v2/ui/Label';
import { Slider } from '@/components/v2/ui/Slider';
import { Switch } from '@/components/v2/ui/Switch';
import { usePricingPreview } from '@/hooks/subscription/usePricingPreview';
import { useSubscriptionConfig } from '@/hooks/subscription/useSubscriptionConfig';
import { useSubscriptionUsage } from '@/hooks/subscription/useSubscriptionUsage';
import { SubscriptionBookingModal } from './SubscriptionBookingModal';
interface TierConfig {
  id: 'free' | 'starter' | 'pro' | 'enterprise';
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  bgColor: string;
  borderColor: string;
  popular?: boolean;
  pricingModel: 'credit' | 'dimension';
  basePrice?: number; // For Free tier
  features: string[];
}

const TIER_CONFIGS: TierConfig[] = [
  {
    id: 'free',
    name: 'Free',
    icon: Zap,
    iconColor: 'text-neutral-600',
    bgColor: 'bg-neutral-50 dark:bg-neutral-900',
    borderColor: 'border-neutral-200 dark:border-neutral-800',
    pricingModel: 'credit',
    basePrice: 0,
    features: [
      'billing.feature.freeImages',
      'billing.feature.basicAI',
      'billing.feature.communitySupport',
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    icon: Rocket,
    iconColor: 'text-primary-600',
    bgColor: 'bg-primary-50/50 dark:bg-primary-950/20',
    borderColor: 'border-primary-200 dark:border-primary-900',
    pricingModel: 'credit',
    features: [
      'billing.feature.flexibleImages',
      'billing.feature.creditBased',
      'billing.feature.standardAI',
      'billing.feature.emailSupport',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    icon: Sparkles,
    iconColor: 'text-emerald-600',
    bgColor: 'bg-emerald-50/50 dark:bg-emerald-950/20',
    borderColor: 'border-emerald-200 dark:border-emerald-900',
    popular: true,
    pricingModel: 'dimension',
    features: [
      'billing.feature.pimDam',
      'billing.feature.unlimitedIntegrations',
      'billing.feature.multiChannel',
      'billing.feature.flexibleProductLimit',
      'billing.feature.channelPricing',
      'billing.feature.prioritySupport',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    icon: Building2,
    iconColor: 'text-primary-600',
    bgColor: 'bg-primary-50/50 dark:bg-primary-950/20',
    borderColor: 'border-primary-200 dark:border-primary-900',
    pricingModel: 'dimension',
    features: [
      'billing.feature.allFromPro',
      'billing.feature.dedicatedSupport',
      'billing.feature.customIntegrations',
      'billing.feature.advancedAnalytics',
      'billing.feature.whiteLabelOption',
      'billing.feature.apiAccessUnlimited',
      'billing.feature.customFeatures',
    ],
  },
];

// Credits per image (from feature_catalog - should match DB value!)
const CREDITS_PER_IMAGE = 30;
const DEFAULT_IMAGES = Math.round(1500 / CREDITS_PER_IMAGE);

// Helper: Round value to dynamic step based on ranges
function roundToStep(value: number, ranges: { min: number; max: number; step: number }[]): number {
  const range = ranges.find((r) => value >= r.min && value <= r.max);
  if (!range) return value;
  return Math.round(value / range.step) * range.step;
}

// Dynamic step ranges
const IMAGE_RANGES = [
  { min: 10, max: 500, step: 10 },
  { min: 501, max: 1000, step: 50 },
  { min: 1001, max: 5000, step: 100 },
];

const PRODUCT_RANGES = [
  { min: 1000, max: 5000, step: 100 },
  { min: 5001, max: 500000, step: 1000 },
];

export function PricingTiersGrid() {
  const { t } = useTranslation();

  // Current subscription config (to set initial slider values)
  const { data: currentConfig, isLoading: configLoading } = useSubscriptionConfig();
  const { data: currentUsage } = useSubscriptionUsage();

  // DEFAULT_IMAGES is a module-level constant (see above)

  // Derive booked values from current subscription
  // monthly_credits_allocated is the source of truth (not desired_images_monthly which can be stale)
  const bookedCredits = currentUsage?.credits?.limit ?? 0;
  const bookedImages = bookedCredits > 0 ? Math.round(bookedCredits / CREDITS_PER_IMAGE) : 0;
  const bookedProducts = currentConfig?.product_limit ?? 0;
  const activeTier = currentConfig?.pricing_model === 'dimension' ? 'pro' : currentConfig ? 'starter' : null;

  // Data-ready flag: config loaded AND (no subscription OR usage also loaded)
  const dataReady = !configLoading && (!currentConfig || !!currentUsage);

  // Compute initial slider values from subscription data
  const initialImages = dataReady ? (bookedImages > 0 ? bookedImages : DEFAULT_IMAGES) : 0;
  const initialProProducts = dataReady && bookedProducts > 0 && bookedProducts <= 100000 ? bookedProducts : 10000;
  const initialEnterpriseProducts = dataReady && bookedProducts > 100000 ? bookedProducts : 100001;

  // Global configuration — starts from subscription once loaded
  const [globalImages, setGlobalImages] = useState(0);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');

  // State for each tier's configuration
  const [proProducts, setProProducts] = useState(10000);
  const [proChannels] = useState(6); // Fixed at 6, no setter needed
  const [enterpriseProducts, setEnterpriseProducts] = useState(100001);
  const [enterpriseChannels] = useState(6); // Fixed at 6, no setter needed
  const [initialized, setInitialized] = useState(false);

  // Initialize sliders from current subscription (once both config AND usage data exist)
   
  useEffect(() => {
    if (initialized || !dataReady) return;
    setGlobalImages(initialImages);
    setProProducts(initialProProducts);
    setEnterpriseProducts(initialEnterpriseProducts);
    setInitialized(true);
  }, [dataReady, initialized, initialImages, initialProProducts, initialEnterpriseProducts]);

  // Modal state
  const [bookingModal, setBookingModal] = useState<{
    isOpen: boolean;
    config: {
      tierName: string;
      tierCode: string;
      monthlyPrice: number;
      annualPrice: number;
      credits?: number;
      products?: number;
      channels?: number;
    } | null;
  }>({ isOpen: false, config: null });

  // Pricing previews
  const starterPreview = usePricingPreview({
    tierCode: 'starter',
    pricingModel: 'credit',
    desiredCreditsMonthly: globalImages * CREDITS_PER_IMAGE,
    enabled: globalImages > 0,
  });

  const proPreview = usePricingPreview({
    tierCode: 'pro',
    pricingModel: 'dimension',
    desiredCreditsMonthly: globalImages * CREDITS_PER_IMAGE,
    selectedProducts: proProducts,
    selectedChannels: proChannels,
    enabled: proProducts > 0 && proChannels > 0,
  });

  const enterprisePreview = usePricingPreview({
    tierCode: 'enterprise',
    pricingModel: 'dimension',
    desiredCreditsMonthly: globalImages * CREDITS_PER_IMAGE,
    selectedProducts: enterpriseProducts,
    selectedChannels: enterpriseChannels,
    enabled: enterpriseProducts > 0 && enterpriseChannels > 0,
  });

  const handleBooking = (config: TierConfig, monthlyPrice: number) => {
    const annualPrice = monthlyPrice * 10; // 10 months pricing (2 months free)

    // Credits directly from slider value (not from debounced API response)
    const credits = config.id !== 'free' ? globalImages * CREDITS_PER_IMAGE : undefined;

    const tierConfig = {
      tierName: config.name,
      tierCode: config.id,
      monthlyPrice,
      annualPrice,
      credits,
      products: config.id === 'pro' ? proProducts : config.id === 'enterprise' ? enterpriseProducts : undefined,
      channels: config.id === 'pro' ? proChannels : config.id === 'enterprise' ? enterpriseChannels : undefined,
    };

    setBookingModal({ isOpen: true, config: tierConfig });
  };

  const getButtonLabel = (tier: TierConfig): string => {
    if (!activeTier) return t('billing.bookNow');

    const currentCredits = bookedImages * CREDITS_PER_IMAGE;
    const newCredits = globalImages * CREDITS_PER_IMAGE;

    if (tier.id === 'starter' && activeTier === 'starter') {
      if (newCredits > currentCredits) return t('billing.upgradeNow');
      if (newCredits < currentCredits) return t('billing.planDowngrade');
      return t('billing.currentPlan');
    }
    if (tier.id === 'pro' && activeTier === 'pro') {
      if (newCredits > currentCredits || proProducts > bookedProducts) return t('billing.upgradeNow');
      if (newCredits < currentCredits || proProducts < bookedProducts) return t('billing.planDowngrade');
      return t('billing.currentPlan');
    }
    if (tier.id === 'enterprise' && activeTier === 'pro') return t('billing.upgradeNow');
    if (tier.id === 'starter' && activeTier === 'pro') return t('billing.planDowngrade');
    return t('billing.bookNow');
  };

  const renderTierCard = (config: TierConfig) => {
    const Icon = config.icon;

    // Calculate price based on tier
    // Use previous data while fetching (keepPreviousData) — never show 0€
    let monthlyPrice = config.basePrice ?? 0;
    let displayPrice = 0;
    let isLoading = false;
    let isFetching = false;

    if (config.id === 'starter') {
      monthlyPrice = starterPreview.data?.monthly_price_eur ?? monthlyPrice;
      isLoading = starterPreview.isLoading && !starterPreview.isPlaceholderData;
      isFetching = starterPreview.isFetching;
    } else if (config.id === 'pro') {
      monthlyPrice = proPreview.data?.monthly_price_eur ?? monthlyPrice;
      isLoading = proPreview.isLoading && !proPreview.isPlaceholderData;
      isFetching = proPreview.isFetching;
    } else if (config.id === 'enterprise') {
      monthlyPrice = enterprisePreview.data?.monthly_price_eur ?? monthlyPrice;
      isLoading = enterprisePreview.isLoading && !enterprisePreview.isPlaceholderData;
      isFetching = enterprisePreview.isFetching;
    }

    // Check if "Preis auf Anfrage" should be shown
    const showPriceOnRequest =
      (config.id === 'starter' && globalImages > 1000) ||
      (config.id === 'enterprise' && (enterpriseProducts >= 100000 || enterpriseChannels >= 7));

    // Apply billing cycle
    displayPrice = billingCycle === 'annual' ? monthlyPrice * 10 : monthlyPrice;

    // SAFETY: Never show 0€ for paid tiers (API error, CSRF, timeout, etc.)
    const hasPriceError = config.id !== 'free' && displayPrice <= 0 && !showPriceOnRequest;

    return (
      <Card
        key={config.id}
        className={`relative flex flex-col h-full ${config.bgColor} ${config.borderColor} border-2 transition-all hover:shadow-lg`}
      >
        {/* Popular Badge */}
        {config.popular && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <Badge className="bg-emerald-600 text-white px-4 py-1">
              {t('billing.mostPopular')}
            </Badge>
          </div>
        )}

        <CardHeader>
          {/* Icon */}
          <div className={`p-3 ${config.bgColor} rounded-xl w-fit mb-2`}>
            <Icon className={`h-6 w-6 ${config.iconColor}`} />
          </div>

          {/* Tier Name */}
          <CardTitle className="text-2xl">{config.name}</CardTitle>

          {/* Price */}
          <div className="mt-4">
            {isLoading ? (
              <div className="h-12 bg-neutral-200 dark:bg-neutral-800 rounded animate-pulse" />
            ) : showPriceOnRequest ? (
              <div className="space-y-1">
                <div className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">
                  {t('billing.priceOnRequest')}
                </div>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2 italic">
                  {config.id === 'starter'
                    ? t('billing.customPricingStarter')
                    : t('billing.customPricingEnterprise')}
                </p>
              </div>
            ) : (
              <>
                <div className={`flex items-baseline gap-1 transition-opacity duration-200 ${isFetching ? 'opacity-60' : 'opacity-100'}`}>
                  <span className="text-4xl font-bold text-neutral-900 dark:text-neutral-50">
                    {hasPriceError ? '---' : `${displayPrice.toFixed(2).replace('.', ',')} €`}
                  </span>
                  <span className="text-neutral-600 dark:text-neutral-400">
                    {hasPriceError ? t('billing.priceUnavailable') : billingCycle === 'annual' ? t('billing.perYear') : t('billing.perMonth')}
                  </span>
                </div>
                {config.id !== 'free' && billingCycle === 'annual' && (
                  <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-1 font-medium">
                    {t('billing.twoMonthsFree')}
                  </p>
                )}
                {/* Tier Description */}
                {config.id === 'starter' && (
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2 italic">
                    {t('billing.starterTagline')}
                  </p>
                )}
                {config.id === 'pro' && (
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2 italic">
                    {t('billing.proTagline')}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Credits Input (for all tiers except Free) */}
          {config.id !== 'free' && (
            <div className="mt-4 p-3 bg-primary-50/50 dark:bg-primary-950/20 rounded-lg border border-primary-200 dark:border-primary-900">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor={`${config.id}-credits`} className="text-sm font-medium text-primary-900 dark:text-primary-100">
                  {t('billing.creditsPerMonth')}:
                </Label>
                <input
                  id={`${config.id}-credits`}
                  type="number"
                  min={300}
                  max={150000}
                  step={100}
                  value={globalImages * CREDITS_PER_IMAGE}
                  onChange={(e) => {
                    const credits = Number(e.target.value);
                    const images = Math.round(credits / CREDITS_PER_IMAGE);
                    if (images >= 10 && images <= 5000) {
                      setGlobalImages(images);
                    }
                  }}
                  className="w-36 px-3 py-2 text-right text-sm font-medium border border-primary-300 dark:border-primary-700 rounded-md bg-white dark:bg-primary-950 text-primary-900 dark:text-primary-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <p className="text-xs text-primary-700 dark:text-primary-300 mt-1">
                {t('billing.imagesPerMonthLabel', { count: globalImages })}
              </p>
            </div>
          )}
        </CardHeader>

        <CardContent className="flex-grow space-y-6">
          {/* Configuration Info for Starter */}
          {config.pricingModel === 'credit' && config.id === 'starter' && (
            <div className="space-y-2 p-4 bg-white dark:bg-neutral-950 rounded-lg border border-neutral-200 dark:border-neutral-800">
              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-50">
                {t('billing.currentConfig')}
              </p>
              <p className="text-xs text-neutral-600 dark:text-neutral-400">
                {t('billing.imagesCreditsEquation', { images: globalImages.toLocaleString(), credits: (globalImages * CREDITS_PER_IMAGE).toLocaleString() })}
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-500 italic">
                {t('billing.imagesGlobalHint')}
              </p>
            </div>
          )}

          {config.pricingModel === 'dimension' && config.id === 'pro' && (
            <div className="space-y-4 p-4 bg-white dark:bg-neutral-950 rounded-lg border border-neutral-200 dark:border-neutral-800">
              {/* Products Slider */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-4">
                  <Label htmlFor={`${config.id}-products`} className="text-sm font-medium">
                    {t('billing.products')}:
                  </Label>
                  <input
                    type="number"
                    min={1000}
                    max={100000}
                    value={proProducts}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      if (value >= 1000 && value <= 100000) {
                        setProProducts(value);
                      }
                    }}
                    className="w-32 px-3 py-1.5 text-right text-sm border border-neutral-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <Slider
                  id={`${config.id}-products`}
                  min={1000}
                  max={100000}
                  step={1}
                  value={proProducts}
                  onChange={(e) => {
                    const rounded = roundToStep(Number(e.target.value), PRODUCT_RANGES);
                    setProProducts(rounded);
                  }}
                  className="w-full"
                />
                <p className="text-xs text-neutral-500 dark:text-neutral-500 italic">
                  {t('billing.proProductsHint')}
                </p>
              </div>

              {/* Channels (Fixed) */}
              <div className="pt-2 border-t border-neutral-200 dark:border-neutral-800">
                <p className="text-xs text-neutral-600 dark:text-neutral-400">
                  {t('billing.channelsIncluded', { count: proChannels })}
                </p>
              </div>
            </div>
          )}

          {config.pricingModel === 'dimension' && config.id === 'enterprise' && (
            <div className="space-y-4 p-4 bg-white dark:bg-neutral-950 rounded-lg border border-neutral-200 dark:border-neutral-800">
              {/* Products Slider */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-4">
                  <Label htmlFor={`${config.id}-products`} className="text-sm font-medium">
                    {t('billing.products')}:
                  </Label>
                  <input
                    type="number"
                    min={100001}
                    max={500000}
                    value={enterpriseProducts}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      if (value >= 100001 && value <= 500000) {
                        setEnterpriseProducts(value);
                      }
                    }}
                    className="w-32 px-3 py-1.5 text-right text-sm border border-neutral-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <Slider
                  id={`${config.id}-products`}
                  min={100001}
                  max={500000}
                  step={1}
                  value={enterpriseProducts}
                  onChange={(e) => {
                    const rounded = roundToStep(Number(e.target.value), PRODUCT_RANGES);
                    setEnterpriseProducts(rounded);
                  }}
                  className="w-full"
                />
                <p className="text-xs text-neutral-500 dark:text-neutral-500 italic">
                  {t('billing.enterpriseProductsHint')}
                </p>
              </div>

              {/* Channels (Fixed) */}
              <div className="pt-2 border-t border-neutral-200 dark:border-neutral-800">
                <p className="text-xs text-neutral-600 dark:text-neutral-400">
                  {t('billing.channelsIncluded', { count: enterpriseChannels })}
                </p>
              </div>
            </div>
          )}

          {/* Features List */}
          <ul className="space-y-3">
            {config.features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <Check className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-neutral-700 dark:text-neutral-300">
                  {t(feature)}
                </span>
              </li>
            ))}
          </ul>
        </CardContent>

        <CardFooter>
          <Button
            className={
              config.popular
                ? 'w-full bg-primary-500 hover:bg-primary-600 text-white border-0 shadow-lg shadow-primary-500/30 ring-2 ring-primary-500/20 font-semibold'
                : 'w-full'
            }
            variant={config.popular ? 'default' : 'outline'}
            size="lg"
            disabled={config.id === 'free' || isLoading || hasPriceError}
            onClick={() => {
              if (config.id !== 'free' && !showPriceOnRequest) {
                handleBooking(config, monthlyPrice);
              }
            }}
          >
            {config.id === 'free'
              ? t('billing.currentPlan')
              : showPriceOnRequest
                ? t('billing.contactUs')
                : getButtonLabel(config)}
          </Button>
        </CardFooter>
      </Card>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50 mb-2">
          {t('billing.selectPlan')}
        </h2>
        <p className="text-neutral-600 dark:text-neutral-400">
          {t('billing.starterPlan')}
        </p>
      </div>

      {/* Global Configuration: Bilder pro Monat */}
      <Card className={`border-2 border-primary-200 dark:border-primary-900 bg-gradient-to-r from-primary-50/50 to-primary-50/30 dark:from-primary-950/20 dark:to-primary-950/10 ${!initialized ? 'opacity-50 pointer-events-none' : ''}`}>
        <CardContent className="pt-6 pb-6">
          {/* Header: Big Number + Toggle */}
          <div className="flex items-start justify-between mb-6">
            <div className="space-y-2 flex-1">
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                {t('billing.imagesPerMonth')}
              </p>
              <div className="flex items-center gap-4">
                <p className="text-5xl font-bold text-neutral-900 dark:text-neutral-50">
                  {globalImages.toLocaleString()}
                </p>
                <input
                  id="global-images-input"
                  type="number"
                  min={10}
                  max={5000}
                  value={globalImages}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (value >= 10 && value <= 5000) {
                      setGlobalImages(value);
                    }
                  }}
                  className="w-32 px-3 py-1.5 text-right text-sm border border-neutral-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Max. 1000"
                />
              </div>
              <p className="text-sm text-neutral-500 dark:text-neutral-500">
                {t('billing.creditsPerMonthLabel', { count: globalImages * CREDITS_PER_IMAGE })}
              </p>
            </div>

            {/* Billing Cycle Toggle */}
            <div className="flex items-center gap-3 bg-white dark:bg-neutral-950 p-4 rounded-lg border border-neutral-200 dark:border-neutral-800 shadow-sm">
              <Label htmlFor="billing-toggle" className="text-sm font-medium cursor-pointer whitespace-nowrap">
                {t('billing.annualPayment')}
              </Label>
              {billingCycle === 'annual' && (
                <Badge className="bg-emerald-600 text-white text-sm font-semibold px-3 py-1.5 animate-pulse shadow-lg">
                  {t('billing.twoMonthsGift')}
                </Badge>
              )}
              <Switch
                id="billing-toggle"
                checked={billingCycle === 'annual'}
                onCheckedChange={(checked) => setBillingCycle(checked ? 'annual' : 'monthly')}
                className="ml-auto"
              />
            </div>
          </div>

          {/* Slider */}
          <div className="space-y-2">
            <Slider
              id="global-images"
              min={10}
              max={1000}
              step={1}
              value={Math.min(globalImages, 1000)}
              onChange={(e) => {
                const rounded = roundToStep(Number(e.target.value), IMAGE_RANGES);
                setGlobalImages(rounded);
              }}
              className="w-full"
            />
            {/* Scale Labels */}
            <div className="flex justify-between text-xs text-neutral-500 dark:text-neutral-500 px-1">
              <span>10</span>
              <span>250</span>
              <span>500</span>
              <span>750</span>
              <span>1.000</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
        {TIER_CONFIGS.filter((tier) => tier.id !== 'free').map(renderTierCard)}
      </div>

      {/* Booking Modal */}
      {bookingModal.config && (
        <SubscriptionBookingModal
          isOpen={bookingModal.isOpen}
          onClose={() => setBookingModal({ isOpen: false, config: null })}
          config={bookingModal.config}
          billingCycle={billingCycle}
        />
      )}
    </div>
  );
}
